import { onCleanup } from "solid-js";

export interface JobQueueEventHandlers {
    onProcessing?: (message: string, jobQueueGU: string) => void;
    onSuccess?: (status: string, jobQueueGU: string) => void | Promise<void>;
    onError?: (message: string, jobQueueGU: string) => void;
    onComplete?: (status: string, jobQueueGU: string) => void;
}

/**
 * Hook to set up and manage job queue event listeners
 * Automatically cleans up on component unmount
 */
export function useJobQueueEvents(handlers: JobQueueEventHandlers) {
    // Check if jQuery is available
    if (!window.$) {
        console.warn('jQuery not available - job queue events will not be registered');
        return;
    }

    const $ = window.$;
    const namespace = '.attendanceGrid';

    // Listen for report processing updates
    // Event signature: $(document).trigger("ReportProcessing", [ReportName + "<br/>" + Message, JQGU])
    if (handlers.onProcessing) {
        $(document).on(`ReportProcessing${namespace}`, (event: any, message: string, JQGU: string) => {
            console.log('Report processing:', message, JQGU);
            handlers.onProcessing?.(message, JQGU);
        });
    }

    // Listen for successful report completion
    // Event signature: $(document).trigger("ReportSuccess", [Status, JQGU])
    if (handlers.onSuccess) {
        $(document).on(`ReportSuccess${namespace}`, async (event: any, status: string, JQGU: string) => {
            console.log('Report completed successfully:', status, JQGU);
            await handlers.onSuccess?.(status, JQGU);
        });
    }

    // Listen for report errors
    // Event signature: $(document).trigger("ReportError", [Message, JQGU])
    if (handlers.onError) {
        $(document).on(`ReportError${namespace}`, (event: any, message: string, JQGU: string) => {
            console.error('Report error:', message, JQGU);
            handlers.onError?.(message, JQGU);
        });
    }

    // Listen for report complete (fires after success or error)
    // Event signature: $(document).trigger("ReportComplete", [Status, JQGU])
    if (handlers.onComplete) {
        $(document).on(`ReportComplete${namespace}`, (event: any, status: string, JQGU: string) => {
            console.log('Report complete event:', status, JQGU);
            handlers.onComplete?.(status, JQGU);
        });
    }

    // Clean up event listeners when component unmounts
    onCleanup(() => {
        $(document).off(namespace);
    });
}
